import React, { useState, useEffect } from 'react';
import { createStaticRanges } from '../custom-date-range-picker/custom-date-range-picker.component.jsx';
import CustomDateRangePicker from '../custom-date-range-picker/custom-date-range-picker.component.jsx';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import './date-range-picker-with-inputs.styles.scss';
import useDateDefineds from '../../../hooks/date-defineds.hook';
import { isSameDay } from '../../../utils/date-fns-replacement';

const DateRangePickerWithInputs = ({
  startDate,
  endDate,
  onDateChange,
  editableDateInputs = false,
  direction = "vertical",
  startDatePlaceholder = "From",
  endDatePlaceholder = "To",
  dateFormat = "DD/MM/YYYY"
}) => {
  const defineds = useDateDefineds();

  const sideBarOptions = () => {
    const customDateObjects = [
      {
        label: "Current Year",
        range: () => ({
          startDate: defineds.startOfCurrentYear,
          endDate: defineds.endOfToday,
        }),
      },
      {
        label: "Previous Year",
        range: () => ({
          startDate: defineds.startOflastYear,
          endDate: defineds.endOfPreviousYear,
        }),
      },
      {
        label: "Last 12 Months",
        range: () => ({
          startDate: defineds.startOfLastTwelveMonth,
          endDate: defineds.endOfToday,
        }),
      },
      {
        label: "Last 6 Months",
        range: () => ({
          startDate: defineds.startOfLastSixMonth,
          endDate: defineds.endOfToday,
        }),
      },
      {
        label: "Last 90 Days",
        range: () => ({
          startDate: defineds.startOfLastNintyDay,
          endDate: defineds.endOfToday,
        }),
      },
      {
        label: "Last 30 Days",
        range: () => ({
          startDate: defineds.startOfLastThirtyDay,
          endDate: defineds.endOfToday,
        }),
      },
    ];
  
    return customDateObjects;
  };

  
  const sideBar = sideBarOptions();
  
  const staticRanges = [
    ...createStaticRanges(sideBar),
  ];

  const [selectedRange, setSelectedRange] = useState({
    startDate: defineds.startOfLastTwelveMonth,
    endDate: defineds.endOfToday,
    key: "selection",
  });

  const [inputDates, setInputDates] = useState({
    startDate: startDate ? moment(startDate).format(dateFormat) : '',
    endDate: endDate ? moment(endDate).format(dateFormat) : ''
  });

  const [tempInputValues, setTempInputValues] = useState({
    startDate: startDate ? moment(startDate).format(dateFormat) : '',
    endDate: endDate ? moment(endDate).format(dateFormat) : ''
  });

  const convertTwoDigitYear = (dateStr) => {
    const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
    if (match) {
      const [, day, month, year] = match;
      const fullYear = parseInt(year) + 2000;
      return `${day}/${month}/${fullYear}`;
    }
    return dateStr;
  };

  useEffect(() => {
    setSelectedRange({
      startDate: startDate ? new Date(startDate) : null,
      endDate: endDate ? new Date(endDate) : null,
      key: 'selection'
    });
    const formattedStartDate = startDate ? moment(startDate).format(dateFormat) : '';
    const formattedEndDate = endDate ? moment(endDate).format(dateFormat) : '';
    
    setInputDates({
      startDate: formattedStartDate,
      endDate: formattedEndDate
    });
    setTempInputValues({
      startDate: formattedStartDate,
      endDate: formattedEndDate
    });
  }, [startDate, endDate, dateFormat]);

  const handleRangeChange = (ranges) => {
    const newRange = ranges.selection;
    
    if (newRange.startDate && newRange.endDate && moment(newRange.startDate).isAfter(newRange.endDate)) {
      return;
    }
    
    const formattedStartDate = newRange.startDate ? moment(newRange.startDate).format(dateFormat) : '';
    const formattedEndDate = newRange.endDate ? moment(newRange.endDate).format(dateFormat) : '';

    if (formattedStartDate !== inputDates.startDate || formattedEndDate !== inputDates.endDate) {
      setSelectedRange(newRange);
      setInputDates({
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });
      setTempInputValues({
        startDate: formattedStartDate,
        endDate: formattedEndDate
      });

      let label = "Custom";
      staticRanges.forEach(range => {
        const rangeValue = range.range();
        if (isSameDay(newRange.startDate, rangeValue.startDate) &&
            isSameDay(newRange.endDate, rangeValue.endDate)) {
          label = range.label;
        }
      });

      onDateChange(newRange, label);
    }
  };

  const handleInputChange = (e, field) => {
    const { value } = e.target;
    setTempInputValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDateUpdate = (field) => {
    let value = tempInputValues[field];
    
    if (value === inputDates[field]) {
      return;
    }

    value = convertTwoDigitYear(value);

    const date = moment(value, dateFormat, true);
    
    if (date.isValid()) {
      if (field === 'startDate' && selectedRange.endDate && date.isAfter(selectedRange.endDate)) {
        setTempInputValues(prev => ({
          ...prev,
          [field]: inputDates[field]
        }));
        return;
      }
      if (field === 'endDate' && selectedRange.startDate && date.isBefore(selectedRange.startDate)) {
        setTempInputValues(prev => ({
          ...prev,
          [field]: inputDates[field]
        }));
        return;
      }

      const newRange = {
        ...selectedRange,
        [field]: date.toDate()
      };
      
      const formattedValue = date.format(dateFormat);
      
      setSelectedRange(newRange);
      setInputDates(prev => ({
        ...prev,
        [field]: formattedValue
      }));
      setTempInputValues(prev => ({
        ...prev,
        [field]: formattedValue
      }));

      let label = "Custom";
      staticRanges.forEach(range => {
        const rangeValue = range.range();
        if (isSameDay(newRange.startDate, rangeValue.startDate) &&
            isSameDay(newRange.endDate, rangeValue.endDate)) {
          label = range.label;
        }
      });

      onDateChange(newRange, label);
    } else {
      setTempInputValues(prev => ({
        ...prev,
        [field]: inputDates[field]
      }));
    }
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      handleDateUpdate(field);
      if (e.key === 'Tab') {
        const nextInput = e.target.form?.elements[field === 'startDate' ? 1 : 0];
        nextInput?.focus();
      }
    }
  };

  return (
    <div className="date-range-picker-with-inputs">
      <div className="date-inputs">
        <Form.Group>
          <Form.Control
            type="text"
            placeholder={startDatePlaceholder}
            value={tempInputValues.startDate}
            onChange={(e) => handleInputChange(e, 'startDate')}
            onBlur={() => handleDateUpdate('startDate')}
            onKeyDown={(e) => handleKeyDown(e, 'startDate')}
          />
        </Form.Group>
        <Form.Group>
          <Form.Control
            type="text"
            placeholder={endDatePlaceholder}
            value={tempInputValues.endDate}
            onChange={(e) => handleInputChange(e, 'endDate')}
            onBlur={() => handleDateUpdate('endDate')}
            onKeyDown={(e) => handleKeyDown(e, 'endDate')}
          />
        </Form.Group>
      </div>
      <CustomDateRangePicker
        startDate={selectedRange.startDate}
        endDate={selectedRange.endDate}
        onDateChange={handleRangeChange}
        staticRanges={staticRanges}
        editableDateInputs={false}
        direction={direction}
        dateFormat={dateFormat}
        startDatePlaceholder={startDatePlaceholder}
        endDatePlaceholder={endDatePlaceholder}
        showDateDisplay={true}
        showPreview={true}
      />
      <div
        className="f-12 pointer clearBlockHeight f-400"
        onClick={() => {
          handleRangeChange({
            selection: {
              startDate: defineds.startOfLastTwelveMonth,
              endDate: defineds.endOfToday,
            }
          });
        }}
        style={{
          padding: "0.7rem",
        }}
      >
        Clear
      </div>
    </div>
  );
};

export default DateRangePickerWithInputs; 